// LandingPage.js
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Papa from 'papaparse';
import Banner from './Banner';
import RevealText from './RevealText';
import RotatingCircleImages from './RotatingCircleImages';
// import CandleLightEffect from './CandleLightEffect';
// import Footer from './Footer';
import { getSealuminatiShadow } from './helpers/textShadowHelper';
import sealuminatiScare from '../assets/sealuminati_laser.gif';
import '../styles/JumpScare.css';
import SlidingNFTGrid from './SlidingNFTGrid';
import Team from '../components/Team.js';
import roadmapGif from '../assets/roadmap.gif';


const LandingPage = () => {
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    const overlayRef = useRef(null); // inside the LandingPage

    const [address, setAddress] = useState('');
    const [isCandleOn, setIsCandleOn] = useState(true);

    // Jump scare logic
    const [showJumpScare, setShowJumpScare] = useState(false);
    const [hasScared, setHasScared] = useState(false);

    // For the reveal text
    const textRef = useRef(null);
    const [isTextHovered, setIsTextHovered] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleScroll = useCallback(() => {
        if (!isCandleOn && !hasScared) {
            const scrollY = window.scrollY;
            if (scrollY > 1000) {
                setShowJumpScare(true);
                setHasScared(true);
            }
        }
    }, [isCandleOn, hasScared]);

    // This is the callback we pass into CandleLightEffect
    const handleFlashlightMove = useCallback((e) => {
        if (overlayRef.current) {
            overlayRef.current.style.pointerEvents = 'none';
        }
        const elUnderPointer = document.elementFromPoint(e.clientX, e.clientY);
        if (overlayRef.current) {
            overlayRef.current.style.pointerEvents = 'auto';
        }

        if (textRef.current && textRef.current.contains(elUnderPointer)) {
            setIsTextHovered(true);
        } else {
            setIsTextHovered(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [handleScroll]);

    const handleAnimationEnd = () => {
        setShowJumpScare(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        try {
            const response = await fetch('/allowlist.csv');
            if (!response.ok) throw new Error('Network response was not ok');
            const csvData = await response.text();
            Papa.parse(csvData, {
                header: true,
                complete: (results) => {
                    const isWhitelisted = results.data.some(
                        (row) =>
                            row.Address.trim().toLowerCase() ===
                            address.trim().toLowerCase()
                    );
                    if (isWhitelisted) {
                        console.log(`sealuminati secured for ${address}`);
                    } else {
                        console.log(`${address} Not on the whitelist`);
                    }
                },
            });
        } catch (error) {
            console.log(`Error checking whitelist: ${error.message}`);
        }
    };

    const toggleCandleMode = () => {
        setIsCandleOn((prev) => !prev);
    };

    const isMobile = windowSize.width < 768;

    return (
        <div className="relative font-um flex flex-col min-h-screen">
            {/* Candle Light Effect disabled */}
            {/**
            <CandleLightEffect
                isCandleOn={isCandleOn}
                onFlashlightMove={handleFlashlightMove}
                overlayRef={overlayRef}
            />
            **/}

            {/* Jump Scare Overlay */}
            {/* {showJumpScare && (
                <div className="fixed top-0 left-0 w-screen h-screen flex items-center justify-center z-[999999] bg-transparent">
                    <img
                        src={sealuminatiScare}
                        alt="Scary!"
                        className="jump-scare-img max-w-[90%] max-h-[90%] object-contain"
                        onAnimationEnd={handleAnimationEnd}
                    />
                </div>
            )} */}

            {/* Banner */}
            <Banner onToggleCandle={toggleCandleMode} />

            <main className="flex-grow">
                {/*
                  LANDING PAGE SECTION

                  We remove large top-padding so RevealText
                  sits closely "just below" the banner.
                */}
                <section
                    className="
                        relative 
                        flex flex-col 
                        items-center 
                        text-center 
                        select-none 
                        px-4
                        pt-4
                        sm:pt-6
                        mb-0
                        sm:mb-12
                    "
                >
                    {/* Reveal Text just below Banner */}
                    <RevealText
                        ref={textRef}
                        isCandleOn={isCandleOn}
                        normalText="sealuminati "
                        hiddenText="clues within"
                        forceHover={isTextHovered}
                        className="
                            mt-20
                            md:mt-44 
                            md:-mb-20
                            w-full
                            z-10
                            font-bold
                            pointer-events-auto
                            text-[clamp(24px,13vw,135px)]
                            md:text-[clamp(40px,15vw,175px)]
                            leading-[1.1]
                            px-2
                        "
                        style={{
                            color: '#200052',
                            textShadow: getSealuminatiShadow(isMobile),
                        }}
                    />

                    {/* Rotating Circle Images just below Reveal Text */}
                    <div className="mt-6">
                        <RotatingCircleImages />
                    </div>
                </section>

                {/*
                  ABOUT SECTION

                  Remove negative margin top so it
                  appears below the rotating circle naturally.
                */}
                <section
                    className="
                        about 
                        font-imfellenglish 
                        relative 
                        w-full 
                        flex 
                        flex-col 
                        items-center 
                        justify-center 
                        text-center 
                        px-4
                        min-h-0
                        mt-8 
                    "
                >
                    <ul className="about-list list-none p-0 m-0 text-left max-w-screen-lg select-none">
                        <li
                            style={{ fontSize: 'clamp(32px,12vw,150px)' }}
                            className="md:whitespace-nowrap text-center md:text-left"
                        >
                            3669 sealuminati
                        </li>
                        <li>
                            <SlidingNFTGrid />
                        </li>
                        <li
                            style={{ fontSize: 'clamp(32px,12vw,150px)' }}
                            className=""
                        >
                            pssssttt...
                        </li>
                        <li
                            style={{ fontSize: 'clamp(32px,12vw,150px)' }}
                            className=""
                        >
                            pay attention
                        </li>
                        <li
                            style={{
                                fontSize: 'clamp(32px,12vw,150px)',
                                color: '#836EF9',
                            }}
                            className=""
                        >
                            mint asap
                        </li>
                        <li
                            style={{ fontSize: 'clamp(32px,12vw,150px)' }}
                            className=""
                        >
                            we are a cult,
                        </li>
                        <li
                            style={{
                                fontSize: 'clamp(32px,12vw,150px)',
                                color: '#836EF9',
                            }}
                            className="mb-16 md:mb-40"
                        >
                            i love you
                        </li>
                    </ul>
                </section>
                {/* Roadmap image section */}
                <section className="w-full flex flex-col items-center justify-center px-4 mb-12 select-none">
                    <div
                        className="font-imfellenglish text-center leading-none m-0"
                        style={{ fontSize: 'clamp(32px,12vw,150px)' }}
                    >
                        future of the cult
                    </div>
                    <img
                        src={roadmapGif}
                        alt="Roadmap"
                        className="w-full max-w-screen-lg rounded-md shadow-lg mt-4 opacity-70"
                    />
                </section>
                <Team />
            </main>

            {/* Footer with riddle submission disabled */}
            {/** <Footer /> **/}
        </div>
    );
};

export default LandingPage;
